import React from 'react';
import Routes from 'routes';

import type RoastingResult from 'types/utils/roastingResult';
import type { ReasonGet } from 'types/model/reason';
import type { JsonApi } from 'types/utils/jsonApi';

import AuthenticityToken from 'shared/AuthenticityToken/AuthenticityToken';
import SampleQaContainer from 'millingOrders/containers/SampleQaContainer';
import type MillingOutputType from 'millingOrders/types/MillingOutput';
import type { Validations } from 'shared/Roasting/components/RoastingOverlay';

export interface GeneralSampleSectionProps {
  millingOrderId: number;
  generalSample: JsonApi<MillingOutputType>;
  humidityAnalysisAcceptingReasons: ReasonGet[];
  roastingValidations: Validations;
  roastingResults: RoastingResult[];
  roastingSampleWeight: number;
  weight: number;
  cuppingSessionEnabled: boolean;
  // Los analisis ya no se pueden modificar (muestra aprobada).
  locked: boolean;
  // Botones de QA visibles (fracciones confirmadas y permiso de aprobar).
  showQaActions: boolean;
  canFinishQa: boolean;
  canReject: boolean;
}

const QaActionForm = ({
  action,
  label,
  className,
  disabled,
  dataCy,
}: {
  action: string;
  label: string;
  className: string;
  disabled: boolean;
  dataCy: string;
}) => (
  <form action={action} method='post'>
    <AuthenticityToken />
    <input type='hidden' name='_method' value='patch' />
    <button type='submit' className={className} disabled={disabled} data-cy={dataCy}>
      {label}
    </button>
  </form>
);

// PLA-860: en la OTT del flujo nuevo la muestra general (SAM) se analiza y se aprueba junto a las
// fracciones, antes de los completes y del ingreso de subproductos.
const GeneralSampleSection = ({
  millingOrderId,
  generalSample,
  humidityAnalysisAcceptingReasons,
  roastingValidations,
  roastingResults,
  roastingSampleWeight,
  weight,
  cuppingSessionEnabled,
  locked,
  showQaActions,
  canFinishQa,
  canReject,
}: GeneralSampleSectionProps) => (
  <div className='content' data-cy='general_sample_section'>
    <h3>{I18n.translate('fractions.general_sample.title')}</h3>

    <fieldset disabled={locked}>
      <table>
        <thead>
          <tr>
            <th className='table-border' />
            <th className='table-border align-center'>
              {I18n.translate('milling_orders.qa.humidity')}
            </th>
            <th className='table-border align-center'>
              {I18n.translate('milling_orders.qa.physical')}
            </th>
            <th className='table-border align-center'>
              {I18n.translate('milling_orders.qa.roasting')}
            </th>
            <th className='table-border align-center'>
              {I18n.translate('milling_orders.qa.sensorial')}
            </th>
            <th className='align-center'>{I18n.translate('milling_orders.qa.weight')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <SampleQaContainer
              millingOutput={generalSample}
              humidityAnalysisAcceptingReasons={humidityAnalysisAcceptingReasons}
              roastingValidations={roastingValidations}
              roastingResults={roastingResults}
              roastingSampleWeight={roastingSampleWeight}
              weight={weight}
              cuppingSessionEnabled={cuppingSessionEnabled}
            />
          </tr>
        </tbody>
      </table>
    </fieldset>

    {showQaActions && (
      <div className='l-distribute-l-r'>
        <p>{I18n.translate('fractions.general_sample.hint')}</p>

        {canReject ? (
          <QaActionForm
            action={Routes.produce_milling_order_path(millingOrderId)}
            label={I18n.translate('buttons.reject_qa')}
            className='button button--required'
            disabled={false}
            dataCy='reject_qa_button'
          />
        ) : (
          <QaActionForm
            action={Routes.qualify_milling_order_path(millingOrderId)}
            label={I18n.translate('buttons.finish_qa')}
            className='button'
            disabled={!canFinishQa}
            dataCy='finish_qa_button'
          />
        )}
      </div>
    )}
  </div>
);

export default GeneralSampleSection;
